import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Check, X } from "lucide-react";

import type { AfstandVraag } from "../../afstand/afstandApi";
import { stappenVan, vraagTekst, vragenVanStap, zichtbaar } from "../../afstand/afstandDieren";

/**
 * De afstandsvragen van één dier, als venster.
 *
 * <p><b>Waarom een venster en niet een scherm.</b> De vragen horen bij dít dier. Zet je ze onder
 * elkaar op de pagina, dan krijg je bij twee dieren een lijst waarin niemand meer ziet welk antwoord
 * bij welk dier hoort — en dat is precies het soort fout dat pas bij het asiel opvalt.
 *
 * <p><b>Sluiten is niet weggooien.</b> Wat is ingevuld blijft staan, ook halverwege. Iemand die dit
 * formulier invult moet er papieren bij zoeken en soms eerst overleggen; hem laten herbeginnen omdat
 * hij het venster sloot zou wreed zijn.
 *
 * <p><b>De stappen komen van het asiel.</b> De groepen van de vragenlijst zijn de stappen. Een vaste
 * lijst van vier zou betekenen dat een vijfde groep die een asiel aanmaakt stil achteraan geplakt
 * wordt of verdwijnt. De standaardlijst heeft er vier, dus standaard zie je er vier.
 *
 * <p><b>Vervolgvragen verschijnen pas als ze ergens over gaan.</b> "Waar beet het dier?" is een
 * onbeleefde vraag aan iemand wiens dier nooit gebeten heeft, en verplicht gesteld blokkeert hij de
 * aanvraag van iemand die hem niet kán beantwoorden.
 */
export function AfstandVragenWizard({ dierNaam, vragen, antwoorden, onBewaar, onSluit }: {
  dierNaam: string;
  vragen: AfstandVraag[];
  antwoorden: Record<string, string>;
  /** Elke stap bewaart; het venster houdt geen eigen waarheid vast. */
  onBewaar: (antwoorden: Record<string, string>, afgerond: boolean) => void;
  onSluit: () => void;
}) {
  const stappen = stappenVan(vragen);
  const [stap, setStap] = useState(0);
  const [concept, setConcept] = useState<Record<string, string>>(antwoorden);
  // Of de ontbrekende antwoorden al aangewezen mogen worden. Pas na een klik op Volgende: een
  // formulier dat meteen rood kleurt op velden waar je nog niet geweest bent, voelt als een verwijt.
  const [toon, setToon] = useState(false);

  // Escape sluit het venster; dat verwacht iedereen, en het bewaart wat er staat.
  useEffect(() => {
    const toets = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onBewaar(concept, false);
        onSluit();
      }
    };
    globalThis.addEventListener?.("keydown", toets);
    return () => globalThis.removeEventListener?.("keydown", toets);
  }, [concept, onBewaar, onSluit]);

  // Alle hooks staan hierboven: een vroege return ertussen zou de volgorde per beurt laten
  // verschillen, en dan haalt React de ene staat door de andere.
  if (stappen.length === 0) {
    return null;
  }

  const huidige = stappen[stap];
  const vanStap = vragenVanStap(vragen, huidige).filter((v) => zichtbaar(v, concept));
  const ontbreekt = vanStap.filter((v) => v.verplicht && (concept[v.id] ?? "").trim() === "");
  const laatste = stap === stappen.length - 1;
  const voortgang = Math.round(((stap + 1) / stappen.length) * 100);

  const zet = (id: string, waarde: string) => setConcept((e) => ({ ...e, [id]: waarde }));

  const verder = () => {
    if (ontbreekt.length > 0) {
      setToon(true);
      return;
    }
    if (laatste) {
      onBewaar(concept, true);
      onSluit();
      return;
    }
    onBewaar(concept, false);
    setToon(false);
    setStap(stap + 1);
  };

  return (
    <div className="afst__venster" role="dialog" aria-modal="true" aria-label={`Afstandsvragen ${dierNaam}`}>
      <div className="afst__venster-blad">
        <header className="afst__venster-kop">
          <div>
            <h3>Afstandsvragen · {dierNaam}</h3>
            <p>Beantwoord de vragen stap voor stap. De antwoorden worden opgeslagen bij dit dier.</p>
          </div>
          <button type="button" className="afst__venster-sluit" aria-label="Sluiten"
                  onClick={() => { onBewaar(concept, false); onSluit(); }}>
            <X size={20} aria-hidden="true" />
          </button>
        </header>

        <ol className="afst__wstappen">
          {stappen.map((naam, i) => (
            <li key={naam} className={i === stap ? "is-nu" : i < stap ? "is-klaar" : undefined}>
              <span className="afst__wstapnr" aria-hidden="true">
                {i < stap ? <Check size={13} /> : i + 1}
              </span>
              {naam}
            </li>
          ))}
        </ol>

        <div className="afst__wbalk">
          <span>Stap {stap + 1} van {stappen.length}</span>
          <span className="afst__wbalk-spoor"><span style={{ width: `${voortgang}%` }} /></span>
          <span>{voortgang}%</span>
        </div>

        <div className="afst__venster-inhoud">
          <p className="afst__wgroep">{stap + 1}. {huidige}</p>

          {vanStap.map((v) => (
            <Vraagveld
              key={v.id}
              vraag={v}
              waarde={concept[v.id] ?? ""}
              zet={(w) => zet(v.id, w)}
              mist={toon && v.verplicht && (concept[v.id] ?? "").trim() === ""}
            />
          ))}

          <p className="afst__wnoot">
            Het asiel beoordeelt uw aanvraag. Dit formulier betekent nog niet dat het dier
            automatisch wordt overgenomen.
          </p>
        </div>

        <footer className="afst__venster-voet">
          <button type="button" className="afst__knop afst__knop--rustig"
                  onClick={() => { onBewaar(concept, false); onSluit(); }}>
            Opslaan en sluiten
          </button>
          {stap > 0 && (
            <button type="button" className="afst__knop afst__knop--rustig"
                    onClick={() => { onBewaar(concept, false); setToon(false); setStap(stap - 1); }}>
              <ArrowLeft size={18} aria-hidden="true" />
              Vorige
            </button>
          )}
          <button type="button" className="afst__knop" onClick={verder}>
            {laatste ? "Vragen afronden" : "Volgende"}
            <ArrowRight size={18} aria-hidden="true" />
          </button>
        </footer>
      </div>
    </div>
  );
}

/** Eén vraag, in het besturingselement dat het asiel ervoor koos. */
function Vraagveld({ vraag, waarde, zet, mist }: {
  vraag: AfstandVraag;
  waarde: string;
  zet: (w: string) => void;
  mist: boolean;
}) {
  const tekst = vraagTekst(vraag);
  const label = (
    <span className="afst__wlabel">
      {tekst}{vraag.verplicht && <span aria-hidden="true"> *</span>}
      {vraag.toelichting && <small>{vraag.toelichting}</small>}
    </span>
  );

  const invoer = () => {
    switch (vraag.type) {
      case "yesno":
      case "single":
      case "scale": {
        const keuzes = vraag.keuzes.length > 0 ? vraag.keuzes : ["Ja", "Nee"];
        // Keuzerondjes zolang het er weinig zijn: dan zie je alle antwoorden in één oogopslag en
        // hoef je niets open te klappen. Meer dan vier wordt een lijst.
        if (keuzes.length <= 4) {
          return (
            <span className="afst__wkeuzes">
              {keuzes.map((k) => (
                <label key={k} className={`afst__wkeuze${waarde === k ? " is-gekozen" : ""}`}>
                  <input type="radio" name={vraag.id} value={k} checked={waarde === k}
                         onChange={() => zet(k)} />
                  {k}
                </label>
              ))}
            </span>
          );
        }
        return (
          <span className="afst__invoer">
            <select value={waarde} onChange={(e) => zet(e.target.value)}>
              <option value="">Maak een keuze</option>
              {keuzes.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </span>
        );
      }
      case "multi":
        return (
          <span className="afst__wkeuzes afst__wkeuzes--meer">
            {vraag.keuzes.map((k) => {
              const gekozen = waarde.split("|").filter(Boolean);
              const aan = gekozen.includes(k);
              return (
                <label key={k} className={`afst__wkeuze${aan ? " is-gekozen" : ""}`}>
                  <input
                    type="checkbox"
                    checked={aan}
                    onChange={() => zet((aan ? gekozen.filter((x) => x !== k) : [...gekozen, k]).join("|"))}
                  />
                  {k}
                </label>
              );
            })}
          </span>
        );
      case "number":
      case "date":
        return (
          <span className="afst__invoer">
            <input type={vraag.type === "date" ? "date" : "number"} value={waarde}
                   onChange={(e) => zet(e.target.value)} />
          </span>
        );
      case "photo":
        // De foto's van het entingenboekje gaan via hun eigen knop bij het dier; hier zou een
        // tweede, half werkende upload ontstaan.
        return <span className="afst__wnoot">Foto's voegt u toe bij het dier zelf.</span>;
      default:
        return (
          <span className="afst__invoer afst__invoer--tekst">
            <textarea rows={3} value={waarde} onChange={(e) => zet(e.target.value)} />
          </span>
        );
    }
  };

  return (
    <label className={`afst__wveld${mist ? " is-mist" : ""}`}>
      {label}
      {invoer()}
      {mist && <span className="afst__wmist">Deze vraag is nog niet beantwoord.</span>}
    </label>
  );
}
